import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import apiEndpointManager from "../../config/index";
import { PostCategory } from "../../constants/categories";

interface Author {
    authorId: string;
    name: string;
    profileUrl: string;
    profileImageUrl: string;
}

export interface Comment {
    commentId: string;
    author: Author;
    text: string;
    timestamp: string;
    isEdited?: boolean;
}

export interface PostMetadata {
    postDate: string;
    visibility: string;
    hashtags: string[];
    category: PostCategory;
}

export interface PostData {
    timestamp: string;
    id: string;
    author: Author;
    content: {
        text: string;
        media?: Array<{
            mediaType: string;
            mediaUrl: string;
        }>;
    };
    postMetadata: {
        postDate: string;
        visibility: string;
        hashtags: string[];
        category: PostCategory;
    };
    engagement: {
        likesCount: number;
        commentsCount: number;
        shareCount: number;
    };
    comments: Comment[]; // Add this line
}

export type SortOption = "Top" | "Recent";

export interface NewsfeedState {
    posts: PostData[];
    loading: boolean;
    error: string | null;
    postDetail: PostData | null;
    currentPage: number;
    totalPages: number;
    searchQuery: string;
    activeCategories: PostCategory[];
    sortBy: SortOption;
}

const initialState: NewsfeedState = {
    posts: [],
    loading: false,
    error: null,
    postDetail: null,
    currentPage: 1, // Default to 1
    totalPages: 1, // Default to 1
    searchQuery: "", // Initialize search query as empty
    activeCategories: ["All"], // Initialize with "All" category selected by default
    sortBy: "Recent", // Initialize default newsfeed sorting as by Top posts
};

// Async thunk for creating a new post
export const createPost = createAsyncThunk(
    "newsfeed/createPost",
    async (
        postData: {
            author: Author;
            content: {
                text: string;
                media:
                    | Array<{ mediaType: string; mediaUrl: string; thumbnailUrl?: string }>
                    | string[]; // Support both new format and backward compatibility
            };
            postMetadata: Omit<PostMetadata, "postDate" | "visibility">;
        },
        { rejectWithValue },
    ) => {
        try {
            const token = localStorage.getItem("accessToken");
            if (!token) {
                return rejectWithValue("User is not authenticated. No access token found.");
            }

            // The backend now handles both legacy base64 strings and the new media object format.
            // We can pass the media array as-is. The backend will correctly differentiate them.
            const payload = {
                ...postData,
                postMetadata: {
                    ...postData.postMetadata,
                    postDate: new Date().toISOString(),
                    visibility: "public",
                },
            };

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create post");
            }

            const result = await response.json();
            return result.post as PostData; // The backend should return the complete new post object
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Error creating post");
        }
    },
);

// Async thunk to fetch posts
// ✅ **fetchPostsAsync** - Calls API to get fresh data & updates Redux state
export const fetchAllPosts = createAsyncThunk(
    "newsfeed/fetchAllPosts",
    async (_, { rejectWithValue }) => {
        try {
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                throw new Error("User is not authenticated. No access token found.");
            }

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch posts");
            }

            const data = await response.json();
            return data.posts as PostData[];
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Error fetching posts");
        }
    },
);

// Async thunk for fetching posts via pagination
export const fetchPosts = createAsyncThunk(
    "newsfeed/fetchPosts", // Unique action type for Redux
    async (
        {
            page,
            limit,
            sort,
            categories,
        }: {
            page?: number;
            limit?: number;
            sort?: "asc" | "desc";
            categories?: PostCategory[];
        },
        { rejectWithValue },
    ) => {
        try {
            // Retrieve access token from local storage
            const accessToken = localStorage.getItem("accessToken");

            // If the token is missing, throw an error (user is not authenticated)
            if (!accessToken) {
                throw new Error("User is not authenticated. No access token found.");
            }

            // Initialize query parameters as an empty object
            const queryParams: Record<string, string> = {};

            // Add `page` to query params if provided and valid
            if (page !== undefined) {
                if (!Number.isInteger(page) || page < 1) {
                    throw new Error("Invalid page number. Must be a positive integer.");
                }

                queryParams["page"] = page.toString();
            }

            // Add `limit` to query params if provided and valid
            if (limit !== undefined) {
                if (!Number.isInteger(limit) || limit < 1) {
                    throw new Error("Invalid limit. Must be a positive integer.");
                }

                queryParams["limit"] = limit.toString();
            }

            // Add `sort` to query params if provided and valid
            if (sort !== undefined) {
                if (sort !== "asc" && sort !== "desc") {
                    throw new Error("Invalid sort order. Must be 'asc' or 'desc'.");
                }

                queryParams["sort"] = sort;
            }

            /*
                Handle multiple categories. If categories are provided and don't include "All", send them as comma-separated string

                If "All" is included, no need to filter by category
                If "All" is included, we don't add the categories parameter
                to fetch all posts without category filtering
             */
            if (categories && categories.length > 0) {
                // If "All" is included, no need to filter by category
                if (!categories.includes("All")) {
                    // Convert array of categories to comma-separated string for the API
                    queryParams["categories"] = categories.join(",");
                }
            }

            // Convert query parameters into a URL-friendly query string
            const queryString = new URLSearchParams(queryParams).toString();
            const endpoint = await apiEndpointManager.getEndpoint();
            const url = `${endpoint}/posts${queryString ? `?${queryString}` : ""}`;

            // Perform the API request
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch posts. Server returned status: ${response.status}`,
                );
            }

            const data = await response.json();

            return data;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : "An unknown error occurred while fetching posts",
            );
        }
    },
);

// Async thunk to fetch a single post by ID
export const fetchPostById = createAsyncThunk(
    "newsfeed/fetchPostById",
    async (postId: string, { rejectWithValue }) => {
        try {
            console.log(`[newsfeedSlice] fetchPostById - Fetching post ID: ${postId}`);
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                return rejectWithValue("User is not authenticated. No access token found.");
            }

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts/${postId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                let errorMessage = "Failed to fetch post";

                if (response.status === 401) {
                    errorMessage = "Unauthorized. Please log in again.";
                } else if (response.status === 403) {
                    errorMessage = "You do not have permission to access this post.";
                } else if (response.status === 404) {
                    errorMessage = "Post not found.";
                }

                const errorData = await response.text();
                try {
                    const parsedError = JSON.parse(errorData);
                    errorMessage = parsedError?.message || errorMessage;
                } catch {
                    // Ignore JSON parsing error and use default message
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            const post = data.post as PostData;
            console.log(`[newsfeedSlice] fetchPostById - Successfully fetched post:`, post);
            console.log(
                `[newsfeedSlice] fetchPostById - Comment count: ${post.comments?.length || 0}`,
            );
            return post;
        } catch (error: unknown) {
            console.error(`[newsfeedSlice] fetchPostById - Error:`, error);
            return rejectWithValue(error instanceof Error ? error.message : "Error fetching post");
        }
    },
);

export const updatePostAsync = createAsyncThunk(
    "newsfeed/updatePostAsync",
    async (post: Partial<PostData> & { id: string }, { rejectWithValue, getState }) => {
        console.log(`[newsfeedSlice] updatePostAsync - START - Updating post ID: ${post.id}`);
        console.log(`[newsfeedSlice] updatePostAsync - Post data:`, post);
        console.log(`[newsfeedSlice] updatePostAsync - Comment count:`, post.comments?.length || 0);

        // Get the current state to ensure we have the full post
        const state = getState() as RootState;
        const existingPost = state.newsfeed.posts.find((p) => p.id === post.id);

        if (!existingPost) {
            console.warn(
                `[newsfeedSlice] updatePostAsync - Post ${post.id} not found in state, using partial data`,
            );
        }

        // Create a complete post object by merging existing post with updates
        const completePost: PostData = existingPost
            ? { ...existingPost, ...post }
            : (post as PostData);

        console.log(`[newsfeedSlice] updatePostAsync - Complete post object:`, completePost);

        const payload = {
            id: completePost.id,
            updatedPost: completePost,
        };

        try {
            // Retrieve accessToken from local storage
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                throw new Error("User is not authenticated. No access token found.");
            }

            // Order comments by timestamp with newest first
            payload.updatedPost.comments = [...payload.updatedPost.comments].sort((a, b) => {
                const dateA = new Date(a.timestamp).getTime();
                const dateB = new Date(b.timestamp).getTime();
                return dateB - dateA;
            });

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[newsfeedSlice] updatePostAsync - API Error:`, errorText);
                throw new Error(`Failed to update post: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            console.log(`[newsfeedSlice] updatePostAsync - API success response:`, data);

            // Check if the response includes a complete post object
            // If it doesn't, use our complete merged post
            const responsePost =
                data.post && Object.keys(data.post).length > 2 ? data.post : completePost;

            console.log(`[newsfeedSlice] updatePostAsync - Returning post:`, responsePost);
            console.log(`[newsfeedSlice] updatePostAsync - END`);

            return responsePost;
        } catch (error) {
            console.error(`[newsfeedSlice] updatePostAsync - Error:`, error);
            // Still return the complete post object even on API error to preserve UI state
            console.log(`[newsfeedSlice] updatePostAsync - Returning completePost despite error`);
            return completePost;
        }
    },
);

// Added async version of addPost for future API integration
export const addPostAsync = createAsyncThunk(
    "newsfeed/addPostAsync",
    async (post: PostData, { rejectWithValue }) => {
        try {
            // Retrieve accessToken from local storage
            const accessToken = localStorage.getItem("accessToken");

            if (!accessToken) {
                throw new Error("User is not authenticated. No access token found.");
            }

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(post),
            });

            if (!response.ok) {
                throw new Error("Failed to add post");
            }

            const data = await response.json();

            return data.post; // Return the new post data received from the API
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Error adding post");
        }
    },
);

// For postId synchronization across components
export const syncPostDetails = createAsyncThunk(
    "newsfeed/syncPostDetails",
    async (postId: string, { getState, dispatch }) => {
        console.log(
            `[newsfeedSlice] syncPostDetails - START - Syncing details for post ID: ${postId}`,
        );
        const state = getState() as RootState;

        // Check if postDetail exists and is different from the requested postId
        const currentPostDetail = state.newsfeed.postDetail;
        if (currentPostDetail && currentPostDetail.id !== postId) {
            console.log(
                `[newsfeedSlice] syncPostDetails - Current postDetail ID ${currentPostDetail.id} doesn't match requested ID ${postId}`,
            );
        }

        // Find post in the posts array
        const postInArray = state.newsfeed.posts.find((post) => post && post.id === postId);

        // Log the state of posts array for debugging
        console.log(
            `[newsfeedSlice] syncPostDetails - Posts array length:`,
            state.newsfeed.posts.length,
        );
        console.log(`[newsfeedSlice] syncPostDetails - Post found in array:`, !!postInArray);

        // If the post exists in posts array but not in postDetail or they're different, update postDetail
        if (postInArray) {
            console.log(
                `[newsfeedSlice] syncPostDetails - Found post in array with ${postInArray.comments?.length || 0} comments`,
            );

            // Check if the post in array has the same number of comments as postDetail
            if (currentPostDetail && currentPostDetail.id === postId) {
                const arrayCommentCount = postInArray.comments?.length || 0;
                const detailCommentCount = currentPostDetail.comments?.length || 0;

                if (arrayCommentCount !== detailCommentCount) {
                    console.log(
                        `[newsfeedSlice] syncPostDetails - Comment count mismatch: array=${arrayCommentCount}, detail=${detailCommentCount}`,
                    );
                }
            }

            console.log(`[newsfeedSlice] syncPostDetails - END - Returning post from array`);
            // Return the found post to update postDetail
            return postInArray;
        } else {
            console.log(
                `[newsfeedSlice] syncPostDetails - Post not found in array, fetching from API`,
            );
            // If post not in array, fetch it from API
            const result = await dispatch(fetchPostById(postId));
            if (fetchPostById.fulfilled.match(result)) {
                console.log(
                    `[newsfeedSlice] syncPostDetails - END - Returning post from API fetch`,
                );
                return result.payload;
            }
            console.log(`[newsfeedSlice] syncPostDetails - END - API fetch failed, returning null`);
            return null;
        }
    },
);

// Async thunk to delete a post
export const deletePostAsync = createAsyncThunk(
    "newsfeed/deletePostAsync",
    async (postId: string, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("accessToken");
            if (!token) {
                return rejectWithValue("User is not authenticated. No access token found.");
            }

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await fetch(`${endpoint}/posts/deletePost`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ postId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete post");
            }

            // Return the original postId on success for the reducer
            return postId;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : "Error deleting post");
        }
    },
);

export const newsfeedSlice = createSlice({
    name: "newsfeed",
    initialState,
    reducers: {
        // Set search query
        setSearchQuery(state, action: PayloadAction<string>) {
            state.searchQuery = action.payload;
        },

        setSortOption(state, action: PayloadAction<SortOption>) {
            state.sortBy = action.payload;
        },

        // Keeping sync reducers for immediate UI updates
        addPost(state, action: PayloadAction<PostData>) {
            state.posts = state.posts ? [action.payload, ...state.posts] : [action.payload];
        },
        updatePost(state, action: PayloadAction<PostData>) {
            const index = state.posts.findIndex((post) => post.id === action.payload.id);
            if (index !== -1) {
                state.posts[index] = action.payload;
            }
        },
        deletePost(state, action: PayloadAction<string>) {
            state.posts = state.posts.filter((post) => post.id !== action.payload);
        },
        deleteComment(state, action: PayloadAction<{ postId: string; commentId: string }>) {
            const { postId, commentId } = action.payload;
            const post = state.posts.find((post) => post.id === postId);
            if (post) {
                post.comments = post.comments.filter((comment) => comment.commentId !== commentId);
                post.engagement.commentsCount -= 1;
            }
            // Also update postDetail if it's the current post
            if (state.postDetail && state.postDetail.id === postId) {
                state.postDetail.comments = state.postDetail.comments.filter(
                    (comment) => comment.commentId !== commentId,
                );
                state.postDetail.engagement.commentsCount -= 1;
            }
        },

        // Replace setCategory with toggleCategory to handle multiple category selection
        toggleCategory(state, action: PayloadAction<PostCategory>) {
            const category = action.payload;

            // Special case for "All" category
            if (category === "All") {
                // If selecting "All", drop all other categories
                state.activeCategories = ["All"];
                return;
            }

            /*
                If "All" is currently selected and user selects another category,
                remove "All" and add the new category
            */
            if (state.activeCategories.includes("All")) {
                state.activeCategories = [category];
                return;
            }

            // Check if the category is already selected
            const index = state.activeCategories.indexOf(category);

            // If category is not selected, add it
            if (index === -1) {
                state.activeCategories.push(category);
            } else {
                // Otherwise, remove it
                state.activeCategories = state.activeCategories.filter((c) => c !== category);
            }

            // After update, if nothing left, switch to "All"
            if (state.activeCategories.length === 0) {
                state.activeCategories = ["All"];
            }
        },

        // Utility action to set all active categories at once (useful for initialization)
        setActiveCategories(state, action: PayloadAction<PostCategory[]>) {
            state.activeCategories = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchPosts states
            .addCase(fetchPosts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPosts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                // Add null check for action.payload
                if (!action.payload) {
                    console.log(`[newsfeedSlice] fetchPosts.fulfilled - No payload, returning`);
                    return;
                }

                console.log(
                    `[newsfeedSlice] fetchPosts.fulfilled - Got payload with ${action.payload.posts?.length || 0} posts`,
                );
                console.log(
                    `[newsfeedSlice] fetchPosts.fulfilled - Current page in payload: ${action.payload.currentPage}`,
                );
                console.log(
                    `[newsfeedSlice] fetchPosts.fulfilled - Current posts array length: ${state.posts.length}`,
                );

                /*
          🚨 Prevent Duplicate Posts on Initial Fetch

          In development mode, React's StrictMode intentionally invokes useEffect twice,
          which means that the initial API call for page 1 may be executed two times.

          What happens without this check?
          - The first call returns posts (e.g., [A, B, C]) and sets state.posts to [A, B, C].
          - The second call (a duplicate) again returns [A, B, C] and appends them,
            resulting in [A, B, C, A, B, C].

          How does this block of code fix the issue?
          - action.meta.arg.page retrieves the page number from the original arguments passed
            to the async thunk (e.g., { page: 1, limit: 5, sort: "desc" }).
          - When fetching page 1, we replace state.posts entirely with the new posts, ensuring
            that even if the fetch is called twice, the state only holds the latest fetched posts.
          - For subsequent pages (page 2, 3, etc.), we append the new posts to the existing list.

          This guarantees that the initial set of posts is never duplicated, even if the fetch
          for page 1 is executed more than once.

          The code is very simple, but the error took a while to identify and fix 😅
        */
                if (action.meta.arg.page === 1) {
                    console.log(
                        `[newsfeedSlice] fetchPosts.fulfilled - Page 1, replacing entire posts array`,
                    );
                    console.log(
                        `[newsfeedSlice] fetchPosts.fulfilled - Old posts count: ${state.posts.length}, New posts count: ${action.payload.posts.length}`,
                    );

                    if (state.posts.length > 0) {
                        const oldPostIds = state.posts.map((post: PostData) => post.id).slice(0, 5);
                        const newPostIds = action.payload.posts
                            .map((post: PostData) => post.id)
                            .slice(0, 5);
                        console.log(
                            `[newsfeedSlice] fetchPosts.fulfilled - First 5 old post IDs: ${oldPostIds.join(", ")}`,
                        );
                        console.log(
                            `[newsfeedSlice] fetchPosts.fulfilled - First 5 new post IDs: ${newPostIds.join(", ")}`,
                        );
                    }

                    state.posts = action.payload.posts;
                } else {
                    console.log(
                        `[newsfeedSlice] fetchPosts.fulfilled - Page ${action.meta.arg.page}, appending to posts array`,
                    );
                    console.log(
                        `[newsfeedSlice] fetchPosts.fulfilled - Existing posts: ${state.posts.length}, Adding posts: ${action.payload.posts.length}`,
                    );
                    state.posts = [...state.posts, ...action.payload.posts];
                    console.log(
                        `[newsfeedSlice] fetchPosts.fulfilled - Final posts count: ${state.posts.length}`,
                    );
                }

                state.currentPage = action.payload.currentPage;
                state.totalPages = action.payload.totalPages;
            })
            .addCase(fetchPosts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch posts";
            })
            // Handle fetchAllPosts states
            .addCase(fetchAllPosts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllPosts.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                // Add null check for action.payload
                if (action.payload) {
                    state.posts = action.payload;
                }
            })
            .addCase(fetchAllPosts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Handle fetchPostById states
            .addCase(fetchPostById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPostById.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                // Add proper null checking for action.payload
                if (action.payload) {
                    state.postDetail = action.payload;
                    console.log(
                        `[newsfeedSlice] Reducer - fetchPostById.fulfilled:`,
                        action.payload,
                    );

                    const commentCount = action.payload.comments?.length ?? 0;
                    console.log(
                        `[newsfeedSlice] Reducer - Comment count after update: ${commentCount}`,
                    );
                }
            })
            .addCase(fetchPostById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch post";
                console.error(`[newsfeedSlice] Reducer - fetchPostById.rejected:`, action.error);
            })
            // Handle addPostAsync states
            .addCase(addPostAsync.fulfilled, (state, action: PayloadAction<PostData>) => {
                state.loading = false;
                state.posts.unshift(action.payload); // Should add post to the beginning
            })
            .addCase(addPostAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addPostAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to add post";
            })
            // Handle updatePostAsync states
            .addCase(updatePostAsync.fulfilled, (state, action) => {
                if (!action.payload) {
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - No payload, returning`,
                    // );
                    return; // No payload, do nothing.
                }

                const payload = action.payload as PostData; // Type assertion
                // console.log(
                //     `[newsfeedSlice] updatePostAsync.fulfilled - Processing post ID: ${payload.id}`,
                // );

                const index = state.posts.findIndex((p) => p.id === payload.id);

                if (index !== -1) {
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - Post index in array: ${index}`,
                    // );
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - Updating post in posts array at index ${index}`,
                    // );
                    // Create a copy of the post to be updated
                    const updatedPost = { ...state.posts[index], ...payload };
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - Post before update:`,
                    //     {
                    //         ...state.posts[index],
                    //     },
                    // );
                    state.posts[index] = updatedPost;
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - Post after update:`,
                    //     { ...state.posts[index] },
                    // );
                } else {
                    // console.log(
                    //     `[newsfeedSlice] updatePostAsync.fulfilled - Post with ID ${payload.id} not found in posts array. Adding it to prevent loss.`,
                    // );
                    // If post not found, add it to the start of the array
                    state.posts.unshift(payload);
                }

                if (state.postDetail && state.postDetail.id === payload.id) {
                    // console.log(`[newsfeedSlice] updatePostAsync.fulfilled - Updating postDetail`);
                    state.postDetail = { ...state.postDetail, ...payload };
                }
            })
            // Handle deletePostAsync states
            .addCase(deletePostAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deletePostAsync.fulfilled, (state, action: PayloadAction<string>) => {
                state.loading = false;
                state.posts = state.posts.filter((post) => post.id !== action.payload);
                if (state.postDetail && state.postDetail.id === action.payload) {
                    state.postDetail = null;
                }
            })
            .addCase(deletePostAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Add case for syncPostDetails
            .addCase(syncPostDetails.fulfilled, (state, action) => {
                if (!action.payload) {
                    console.log(
                        `[newsfeedSlice] syncPostDetails.fulfilled - No payload, returning`,
                    );
                    return;
                }

                // Create a typed local constant instead of using non-null assertion
                const payload = action.payload;

                console.log(
                    `[newsfeedSlice] syncPostDetails.fulfilled - Updating postDetail with post ID: ${payload.id}`,
                );
                state.postDetail = payload;

                // Also update the post in the posts array if it exists
                const postIndex = state.posts.findIndex((post) => post && post.id === payload.id);
                console.log(
                    `[newsfeedSlice] syncPostDetails.fulfilled - Post index in array: ${postIndex}`,
                );

                if (postIndex !== -1) {
                    console.log(
                        `[newsfeedSlice] syncPostDetails.fulfilled - Also updating post in posts array at index ${postIndex}`,
                    );
                    console.log(
                        `[newsfeedSlice] syncPostDetails.fulfilled - Post before update:`,
                        state.posts[postIndex],
                    );
                    state.posts[postIndex] = { ...payload };
                    console.log(
                        `[newsfeedSlice] syncPostDetails.fulfilled - Post after update:`,
                        state.posts[postIndex],
                    );
                } else {
                    console.error(
                        `[newsfeedSlice] syncPostDetails.fulfilled - Post not found in posts array, adding it to prevent loss`,
                    );
                    // If we've got post details but the post isn't in the array, add it to prevent loss
                    // This will ensure posts don't disappear after commenting
                    state.posts.unshift({ ...payload });
                }
            })
            .addCase(syncPostDetails.rejected, (state, action) => {
                console.error("[newsfeedSlice] syncPostDetails.rejected", action.error);
            })
            // *** NEW: Handle createPost states ***
            .addCase(createPost.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createPost.fulfilled, (state, action: PayloadAction<PostData>) => {
                state.loading = false;
                // Add the new post to the beginning of the array
                state.posts.unshift(action.payload);
            })
            .addCase(createPost.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

// Export actions from the slice
export const {
    addPost,
    updatePost,
    deletePost,
    deleteComment,
    setSearchQuery,
    setSortOption,
    toggleCategory,
    setActiveCategories,
} = newsfeedSlice.actions;

// Export selectors
export const selectNewsfeed = (state: RootState) => state.newsfeed;
export const selectPostDetail = (state: RootState) => state.newsfeed.postDetail;
export const selectSearchQuery = (state: RootState) => state.newsfeed.searchQuery;
export const selectActiveCategories = (state: RootState) => state.newsfeed.activeCategories;

/*
    Helper function to sort posts based by different metrics
    Needs to be updated to make API calls instead of inplace sorting
 */
const sortPosts = (posts: PostData[], sortBy: SortOption) => {
    // Filter out invalid posts first to prevent undefined errors
    const validPosts = posts.filter((post) => post && post.postMetadata && post.engagement);

    switch (sortBy) {
        case "Recent":
            // Sort by date, newest first, with null checking
            return validPosts.sort((a, b) => {
                // Safely check for postMetadata.postDate
                const dateA = a.postMetadata?.postDate
                    ? new Date(a.postMetadata.postDate).getTime()
                    : 0;
                const dateB = b.postMetadata?.postDate
                    ? new Date(b.postMetadata.postDate).getTime()
                    : 0;
                return dateB - dateA;
            });

        case "Top":
            // Sort by likes with null checking
            return validPosts.sort((a, b) => {
                const likesA = a.engagement?.likesCount || 0;
                const likesB = b.engagement?.likesCount || 0;
                return likesB - likesA;
            });

        default:
            return validPosts;
    }
};

// Create a selector for filtered posts
export const selectFilteredPosts = (state: RootState) => {
    const { posts, searchQuery, activeCategories, sortBy } = state.newsfeed;

    let filteredPosts = [...posts];

    // Filter by search query
    if (searchQuery) {
        filteredPosts = filteredPosts.filter((post) =>
            post.content.text.toLowerCase().includes(searchQuery.toLowerCase()),
        );
    }

    // Filter by categories - handle multiple categories
    if (!activeCategories.includes("All")) {
        // Only apply category filtering if "All" is not selected
        filteredPosts = filteredPosts.filter((post) =>
            // Include posts that match ANY of the selected categories
            activeCategories.includes(post.postMetadata.category),
        );
    }
    // If "All" is selected, no category filtering is applied

    // Sort posts
    return sortPosts(filteredPosts, sortBy);
};

export default newsfeedSlice.reducer;
